import React, { useEffect, useMemo, useState } from 'react';
import '../common/searedDialog/searedDialog.css';
import './searedLoader.css';

// Shared rotating captions — rendered in a shuffled order each mount so
// refreshing the page doesn't always surface the same opener. Themed
// around classic cEDH/Commander grievances.
const LOADER_CAPTIONS = [
    'Blowing up all the lands',
    'Strip mine looping the table',
    'Rage quitting',
    'Table flipping',
    'Asking Manuel for his opinion',
    'Getting banned from Discord',
    'Trolling r/edh',
    'Tutoring for the win',
    'Threat-assessing yourself out of the game',
    'NOT Paying the one',
    'Casting Armageddon on turn 1',
    'Countering your only creature',
    'Drawing 20 cards off Necropotence',
    'Looping Worldgorger Dragon',
    'Announcing an infinite combo',
    // 'Extorting the table for life',
    'Milling your library',
    // 'Sacrificing everyone else',
    'Rule-zeroing after the game starts',
    'Asking if Dockside is banned yet',
    'Playing Winter Orb on turn 1',
    'Resolving Rhystic Study',
    'Mental Misstepping your Sol Ring',
    'Chaining wheels',
    'Proxying the Reserved List',
    'Resolving Hullbreacher triggers',
    'Copying your Cyclonic Rift',
    // 'Winning with Jeska\u2019s Will',
    'Windmill-slamming Smothering Tithe',
    'Forgetting a trigger on purpose',
    // 'Brewing a new 75%',
    // 'Removing Thrasios from the zone',
    // 'Calling a judge',
    // 'Getting Hullbreached out of mana',
    // 'Pitching Force of Will to your only wincon',
    'Countering your precon commander with Force of Will',
    'Dredging Golgari Grave-Troll',
    // 'Turbo-Naus-ing at instant speed',
    'Explaining why Blood Moon isn\u2019t stax',
    'Gitrog-dredge-fetch-discard-loop',
    'Telling the table "it\u2019s just good deckbuilding"',
    // 'Counting lethal on a 40-life board',
];

function shuffled(list) {
    const arr = list.slice();
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
}

/**
 * Inline loader using the same seared-ring animation as GenericLoadingDialog.
 * Use this when you want the brand loader visual inside an embedded section
 * (pane, card, panel) rather than as a full-screen modal.
 *
 * Props:
 *   - caption: override the rotating captions with a single static string
 *   - subcaption: small explanation text under the caption
 *   - size: 'small' | 'medium' | 'large' (default 'medium')
 *   - showCaption: false to render just the ring (for tiny inline indicators)
 */
export function SearedLoader({ caption, subcaption, size = 'medium', showCaption = true }) {
    // Freeze a shuffled order per mount so the rotation cycles a random
    // sequence without re-shuffling on every tick (which would just
    // snap to random items and feel jittery).
    const captions = useMemo(() => shuffled(LOADER_CAPTIONS), []);
    const [captionIdx, setCaptionIdx] = useState(0);

    useEffect(() => {
        if (!showCaption || caption) return undefined;
        const handle = setInterval(() => {
            setCaptionIdx((i) => (i + 1) % captions.length);
        }, 1800);
        return () => clearInterval(handle);
    }, [caption, showCaption, captions.length]);

    return (
        <div className={`searedLoader searedLoader--${size}`} role="status">
            <div className="searedDialog__loaderRing" aria-hidden="true" />
            {showCaption && (
                <span className="searedDialog__loaderCaption" aria-live="polite">
                    {caption || captions[captionIdx]}
                </span>
            )}
            {showCaption && subcaption && <span className="searedDialog__loaderSubcaption">{subcaption}</span>}
        </div>
    );
}
